import { assertRelationLoaded } from "@common/helpers/orm";
import { OrganizationEntityEntity } from "@common/types/organizationEntity";
import { Address } from "../../types/embeddable/address";
import {
    Entity,
    EntityBase,
    EntityIndustry,
    EntityProfile,
    EntityProfileType,
    EntityWithUser,
} from "../../types/entity";
import { TaxLocation } from "../../types/taxLocation";
import { isoStringOrNull, unpackDateOrNull, valueOrNull } from "../dto-helpers";
import { UserDto } from "../user/user.dto";

/**
 * Input type for EntityDto constructor.
 * Accepts Base type from entity classes - user may not be loaded.
 */
export type EntityDtoInput = EntityBase;

/**
 * EntityDto - serializes entity without requiring user relation.
 * When entity.user is not loaded, serializes user as null.
 */
export class EntityDto {
    static deserialize(dto: EntityDto): Entity {
        return {
            ...dto,
            user: valueOrNull(dto.user, UserDto.deserialize),
            transactionEnrichmentPausedAt: unpackDateOrNull(
                dto.transactionEnrichmentPausedAt,
            ),
        };
    }

    id: number;
    uuid: string;
    name: string | null;
    address: Address | null;
    bookkeepingYears: number[];
    bookkeepingStartDate: string;
    taxLocations: TaxLocation[];
    onboardingComplete: boolean;
    industry: EntityIndustry;
    iconColor: string;
    logo: string | null;
    isBusiness: boolean;
    isPersonal: boolean;
    user: UserDto | null;
    userId: number | null;
    profile: EntityProfile | null;
    isMockEntity: boolean;
    workspaceId: string;
    openingBalanceSubmitted: boolean;
    reportsReady: boolean;
    transactionEnrichmentPausedAt: string | null;
    federalTaxRate: number;
    legalType: EntityProfileType | null;
    allowAITrainingOnTheirData: boolean;
    hasCustomChartOfAccounts: boolean;
    organizationAssignments?: OrganizationEntityEntity[];

    constructor(entity: EntityDtoInput) {
        this.id = entity.id;
        this.uuid = entity.uuid;
        this.name = entity.name;
        this.address = entity.address;
        this.bookkeepingYears = entity.bookkeepingYears;
        this.bookkeepingStartDate = entity.bookkeepingStartDate;
        this.taxLocations = entity.taxLocations;
        this.onboardingComplete = entity.onboardingComplete;
        this.industry = entity.industry;
        this.iconColor = entity.iconColor;
        this.logo = entity.logo;
        this.isBusiness = entity.isBusiness;
        this.isPersonal = entity.isPersonal;
        this.user = entity.user ? new UserDto(entity.user) : null;
        this.userId = entity.userId;
        this.profile = entity.profile;
        this.isMockEntity = entity.isMockEntity;
        this.workspaceId = entity.workspaceId;
        this.openingBalanceSubmitted = entity.openingBalanceSubmitted;
        this.reportsReady = entity.reportsReady;
        this.transactionEnrichmentPausedAt = isoStringOrNull(
            entity.transactionEnrichmentPausedAt,
        );
        this.federalTaxRate = entity.federalTaxRate;
        this.legalType = entity.legalType;
        this.allowAITrainingOnTheirData = entity.allowAITrainingOnTheirData;
        this.hasCustomChartOfAccounts = entity.hasCustomChartOfAccounts;
        this.organizationAssignments = entity.organizationAssignments;
    }
}

/**
 * EntityWithUserDto - serializes entity with user relation guaranteed loaded.
 * Asserts at runtime that entity.user is loaded. Use in places where
 * entity.user must be present (e.g. personal entity display, transaction detail).
 */
export class EntityWithUserDto extends EntityDto {
    constructor(entity: EntityWithUser) {
        assertRelationLoaded(entity, "user");
        super(entity);
    }
}
